/**
 * Criterion prefix handling.
 *
 * A criterion's score type is not a stored field — the backend infers it from the
 * opening words of the criteria string (`align/common/api_types.py:20`,
 * `is_binary_evaluation`). "Passes if" / "Fails if" produce a pass/fail judgement;
 * "Reward" / "Penalize" produce a 0–1 score. Nothing in the UI used to say so, and the
 * platform could save criteria that the reward endpoint would reject with a 422.
 *
 * These helpers let the editor treat a criterion as `prefix + body`, so the type is an
 * explicit control and whatever we save always carries a valid prefix.
 */

export const CRITERION_PREFIXES = [
  "Reward",
  "Penalize",
  "Passes if",
  "Fails if",
] as const;

export type CriterionPrefix = (typeof CRITERION_PREFIXES)[number];

/** Prefixes the backend treats as binary; everything else is a 0–1 reward score. */
const BINARY_PREFIXES: readonly CriterionPrefix[] = ["Passes if", "Fails if"];

/** Mirrors MAX_EVALUATION_CRITERIA_LENGTH in `align/common/api_types.py`. */
export const MAX_CRITERIA_LENGTH = 4096;

export type ScoreType = "binary" | "continuous";

export interface SplitCriteria {
  /** The recognised prefix, or null when the text doesn't start with one. */
  prefix: CriterionPrefix | null;
  /** Everything after the prefix; the full string when prefix is null. */
  body: string;
}

export function scoreTypeFor(prefix: CriterionPrefix): ScoreType {
  return BINARY_PREFIXES.includes(prefix) ? "binary" : "continuous";
}

export const SCORE_TYPE_LABEL: Record<ScoreType, string> = {
  binary: "Pass / fail",
  continuous: "0–1 score",
};

export const SCORE_TYPE_HINT: Record<ScoreType, string> = {
  binary: "Every evaluation returns a pass or a fail.",
  continuous: "Every evaluation returns a score between 0 and 1.",
};

/** One-line explanation of what each prefix asks the judge to do. */
export const PREFIX_HINT: Record<CriterionPrefix, string> = {
  Reward: "Score higher when the response does this.",
  Penalize: "Score lower when the response does this.",
  "Passes if": "Pass when the condition holds.",
  "Fails if": "Fail when the condition holds.",
};

/**
 * Split a criterion into its prefix and body.
 *
 * A prefix only matches on a word boundary — it must be followed by whitespace or end
 * the string — so "Rewarding the user" is reported as unrecognised rather than being
 * mangled into `Reward` + "ing the user". Criteria written before the type control
 * existed may well be unrecognised; the editor keeps their text intact and asks the user
 * to choose a type.
 */
export function splitCriteria(criteria: string): SplitCriteria {
  const text = criteria ?? "";
  for (const prefix of CRITERION_PREFIXES) {
    if (!text.startsWith(prefix)) continue;
    const rest = text.slice(prefix.length);
    if (rest === "" || /^\s/.test(rest)) {
      return { prefix, body: rest.trim() };
    }
  }
  return { prefix: null, body: text.trim() };
}

/** Rebuild the criteria string the API will receive. */
export function composeCriteria(
  prefix: CriterionPrefix | null,
  body: string
): string {
  const trimmed = (body ?? "").trim();
  if (prefix === null) return trimmed;
  return trimmed ? `${prefix} ${trimmed}` : prefix;
}

/**
 * Why the current draft can't be saved, or null when it can.
 * Ordered so the user fixes the most fundamental problem first.
 */
export function criteriaError(
  prefix: CriterionPrefix | null,
  body: string
): string | null {
  if (prefix === null) return "Choose how this criterion should score.";
  if (!body.trim()) return "Describe what the criterion should look for.";
  const length = composeCriteria(prefix, body).length;
  if (length > MAX_CRITERIA_LENGTH) {
    return `Criteria must be ${MAX_CRITERIA_LENGTH} characters or fewer.`;
  }
  return null;
}
